// frontend/src/hooks/useLinkMode.js
import { useState, useCallback, useRef } from 'react';

/**
 * Hook personnalisé pour gérer le mode liaison de positions
 * @param {Object} params - Paramètres du hook
 * @param {string} params.cursorMode - Mode actuel du curseur
 * @param {Array} params.allPeriods - Toutes les périodes de la session
 * @returns {Object} - État et fonctions pour gérer le mode liaison
 */
export function useLinkMode({ cursorMode, allPeriods }) {
  // État : positions sélectionnées pour la chaîne en cours d'édition
  const [linkChain, setLinkChain] = useState([]);
  // État : notification d'erreur
  const [linkError, setLinkError] = useState(null);
  // État : visibilité de la notification (pour l'animation)
  const [linkErrorVisible, setLinkErrorVisible] = useState(false);
  
  // Refs pour les timers
  const visibilityTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const clearTimerRef = useRef(null);

  /**
   * Vérifie si le mode liaison est actif
   */
  const isLinkModeActive = cursorMode === 'link';

  /**
   * Annule tous les timers en cours
   */
  const clearAllTimers = useCallback(() => {
    if (visibilityTimerRef.current) clearTimeout(visibilityTimerRef.current);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
  }, []);

  /**
   * Affiche une notification d'erreur temporaire
   * @param {string} message - Message d'erreur à afficher
   * @param {number} duration - Durée d'affichage en millisecondes (par défaut 4000ms)
   */
  const showLinkError = useCallback((message, duration = 4000) => {
    // Annuler les timers précédents
    clearAllTimers();
    
    setLinkError(message);
    
    // Déclencher l'animation d'entrée
    visibilityTimerRef.current = setTimeout(() => setLinkErrorVisible(true), 10);
    
    // Auto-fermeture après la durée spécifiée
    hideTimerRef.current = setTimeout(() => {
      setLinkErrorVisible(false);
      // Attendre la fin de l'animation avant de supprimer le message
      clearTimerRef.current = setTimeout(() => setLinkError(null), 300);
    }, duration);
  }, [clearAllTimers]);

  /**
   * Ferme manuellement la notification d'erreur
   */
  const closeLinkError = useCallback(() => {
    clearAllTimers();
    setLinkErrorVisible(false);
    clearTimerRef.current = setTimeout(() => setLinkError(null), 300);
  }, [clearAllTimers]);

  /**
   * Récupère toutes les positions ayant un link_group_id donné
   * @param {string} linkGroupId - ID du groupe de liaisons
   * @returns {Array} - Positions liées
   */
  const getPositionsByLinkGroup = useCallback((linkGroupId) => {
    if (!linkGroupId || !allPeriods) return [];
    
    const linkedPositions = [];
    allPeriods.forEach((period) => {
      period.positions?.forEach((pos) => {
        if (pos.link_group_id === linkGroupId) {
          linkedPositions.push(pos);
        }
      });
    });
    
    return linkedPositions;
  }, [allPeriods]);

  /**
   * Ajoute ou retire une position de la chaîne en cours d'édition
   * Si la position cliquée a un link_group_id, ajoute toutes les positions du groupe
   * @param {Object} position - Position à ajouter/retirer
   */
  const togglePositionInChain = useCallback((position) => {
    setLinkChain((prev) => {
      const isAlreadyInChain = prev.some((p) => p.id === position.id);
      
      if (isAlreadyInChain) {
        // Retirer de la chaîne
        return prev.filter((p) => p.id !== position.id);
      } else {
        // Si la position a un link_group_id, récupérer toutes les positions du groupe
        let positionsToAdd = [position];
        
        if (position.link_group_id) {
          const groupPositions = getPositionsByLinkGroup(position.link_group_id);
          positionsToAdd = groupPositions.length > 0 ? groupPositions : [position];
        }

        // Vérifier les conflits : plusieurs positions de la même période
        const newChain = [...prev, ...positionsToAdd];
        const periodCounts = {};
        
        newChain.forEach((pos) => {
          // Trouver la période de cette position
          const period = allPeriods?.find(p => 
            p.positions?.some(ppos => ppos.id === pos.id)
          );
          
          if (period) {
            periodCounts[period.id] = (periodCounts[period.id] || 0) + 1;
          }
        });

        // Vérifier s'il y a des doublons de période
        const hasDuplicate = Object.values(periodCounts).some(count => count > 1);
        
        if (hasDuplicate) {
          showLinkError('⚠️ Impossible : la chaine de liaison de cette position entre en conflit avec les positions de la chaîne en cours d\'édition', 5000);
          return prev; // Ne pas ajouter
        }

        // Filtrer les positions déjà présentes pour éviter les doublons
        const newPositions = positionsToAdd.filter(
          newPos => !prev.some(p => p.id === newPos.id)
        );
        
        return [...prev, ...newPositions];
      }
    });
  }, [allPeriods, getPositionsByLinkGroup, showLinkError]);

  /**
   * Vérifie si une position est dans la chaîne en cours d'édition
   * @param {number} positionId - ID de la position
   * @returns {boolean}
   */
  const isPositionInChain = useCallback((positionId) => {
    return linkChain.some((p) => p.id === positionId);
  }, [linkChain]);

  /**
   * Réinitialise la chaîne en cours d'édition
   */
  const clearLinkChain = useCallback(() => {
    setLinkChain([]);
  }, []);

  /**
   * Remplace directement la chaîne par un nouveau tableau de positions
   * @param {Array} positions - Nouvelles positions pour la chaîne
   */
  const setLinkChainDirectly = useCallback((positions) => {
    setLinkChain(positions);
  }, []);

  return {
    // État
    linkChain,
    isLinkModeActive,
    linkError,
    linkErrorVisible,
    
    // Fonctions
    togglePositionInChain,
    isPositionInChain,
    clearLinkChain,
    setLinkChainDirectly,
    getPositionsByLinkGroup,
    closeLinkError,
    showLinkError,
  };
}

